import { Component, Output, EventEmitter, Input, OnDestroy, AfterViewInit, ChangeDetectorRef, SimpleChanges, OnChanges, } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { Store } from "@ngxs/store";
import { Schedule } from "src/app/components/schedules/models/schedule";
import { ScheduleType } from "src/app/components/schedules/models/schedule-type";
import { utils } from "src/app/modules/libs/utils";
import { ScheduleStatusEnum } from "src/app/components/schedules/models/enums";
import { set } from "lodash";

@Component({
  selector: "cl-editor-schedule",
  templateUrl: "./cl-editor-schedule.component.html",
  styleUrls: ["./cl-editor-schedule.component.scss"],
})
export class ClEditorScheduleComponent implements OnDestroy, AfterViewInit, OnChanges {

  @Input() disabled!: boolean;
  @Input() configuration?: string;
  @Input() scheduleTypeId?: number;
  @Input() scheduleResourceId?: number;
  @Input() showLocation: boolean = true;
  @Output() scheduleChanged = new EventEmitter<ScheduleObject>();

  scheduleType?: ScheduleType;
  scheduleDatas: ScheduleData[] = [];

  scheduleDatasFiltered: ScheduleData[] = [];
  scheduleDataEdit: ScheduleData = {};
  tmpSchedId!: number;

  scheduleTypes$!: Observable<ScheduleType[]>;
  scheduleTypes!: ScheduleType[];

  schedules$!: Observable<Schedule[]>;
  schedules!: Schedule[];

  scheduleTypesSubs!: Subscription;
  schedulesSubs!: Subscription;

  scheduleData?: ScheduleData;
  resourceID?: number;

  constructor(private store: Store) { }

  ngOnDestroy(): void {
    this.scheduleTypesSubs.unsubscribe();
    this.schedulesSubs.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.configuration) {
      const config = JSON.parse(this.configuration);
      this.scheduleTypeId = config.scheduleType;
      this.resourceID = config.resourceId;
    }
    this.ngAfterViewInit();
  }

  ngAfterViewInit() {
    this.scheduleTypes$ = this.store.select(
      (state) => state.ScheduleTypes.data
    );
    this.scheduleTypesSubs = this.scheduleTypes$.subscribe((data) => {
      this.scheduleTypes = data;
      this.scheduleType = data.find((x) => x.id == this.scheduleTypeId);
      this.getSchedules();
    });
  }

  getSchedules() {
    this.schedules$ = this.store.select((state) => state.Schedules.data);
    this.schedulesSubs = this.schedules$.subscribe((data) => {
      const scheduleDatas = data
        .filter((s) => s.statusId !== 2 && s.typeId === this.scheduleType?.id)
        .flatMap((schedule) =>
          schedule.scheduleResources.map((sr) => ({
            scheduleId: schedule.id,
            scheduleResourcesId: sr.id,
            name: sr.resourceId ? sr.resource?.name : sr.resourceString,
            resourceId: sr.resourceId ?? 0,
            dateValue1: schedule.lastCompleted,
            dateValue2: schedule.nextDue,
            dateValue3: schedule.extendedDate,
          } as ScheduleData))
        );

      const beamlineResources = scheduleDatas
        .filter((x) => x.resourceId !== 0)
        .sort((a, b) => utils.SortBeamlines(a.name, b.name));

      const stringResources = scheduleDatas.filter((x) => x.resourceId === 0);

      this.scheduleDatas = [...beamlineResources, ...stringResources];

      const schedule = this.getScheduleBySR(this.scheduleResourceId, data);

      this.scheduleData = this.scheduleDatas.find(
        (x) => x.resourceId === schedule?.scheduleResources.find((sr) => sr.id === this.scheduleResourceId || sr.resourceId === this.scheduleResourceId)?.resourceId
      );

      this.scheduleDatasFiltered = utils.cloneDeep(this.scheduleDatas);
    });
  }

  getScheduleBySR(scheduleResourceId: number | undefined, schedules: Schedule[] | undefined): Schedule | undefined {
    // Start with the schedule that contains the selected schedule resource id.
    let schedule = schedules?.find(x => x.scheduleResources.map(r => r.id).includes(scheduleResourceId));
    let scheduleResources = schedule?.scheduleResources.find(r => r.id == scheduleResourceId);

    if (!schedule) {
      // When no match is found, attempt to find a schedule that contains the same resource id and is not completed, but only if the workflow allows completed items to be replaced by active ones.
      schedule = schedules?.find(x => x.scheduleResources.map(r => r.resourceId).includes(scheduleResourceId) && x.statusId != ScheduleStatusEnum.completed);
      scheduleResources = schedule?.scheduleResources.find(r => r.resourceId == scheduleResourceId);
    }

    return schedule;
  }

  displayType(e: any) {
    return e ? e.acronym : null;
  }

  displayLocation(e: any) {
    return e ? e.name : null;
  }

  async scheduleTypeSelected() {
    this.scheduleDatas = [];
    this.getSchedules();
    this.emit();
  }

  locationSelected() {
    this.emit();
  }

  emit() {
    const scheduleObject = new ScheduleObject();
    scheduleObject.scheduleType = this.scheduleType;
    scheduleObject.scheduleData = this.scheduleData;
    this.scheduleChanged.emit(scheduleObject);
  }
}
export class ScheduleObject {
  scheduleType?: ScheduleType;
  scheduleData?: ScheduleData;
}

export class ScheduleData {
  scheduleId?: number;
  scheduleResourcesId?: number;
  resourceId?: number;
  name?: string | null;
  dateValue1?: Date;
  dateValue2?: Date;
  dateValue3?: Date;
}